import { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Aviso } from '@/components/ui/aviso';
import { CeldaProducto } from '@/components/ui/celda-producto';
import { Cifra, Cifras } from '@/components/ui/cifras';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { Etiqueta, type TonoDeEtiqueta } from '@/components/ui/etiqueta';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { Pagination } from '@/components/ui/pagination';
import { CambioDePrecio, Precio, sentidoDe } from '@/components/ui/precio';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { cn } from '@/lib/utils/cn';
import {
  cantidadDe,
  formatoCantidad,
  formatoNumero,
  formatoPorcentaje,
  formatoPrecio,
} from '@/lib/utils/formato-precio';
import { PorQueEstePrecio } from '@/features/pricing-rules/components/por-que-este-precio';
import { desdeFilaSimulada } from '@/features/pricing-rules/lib/recorrido-del-precio';
import { usePedidoConEspera } from '@/features/pricing-rules/lib/use-pedido-con-espera';
import { haceCuanto } from '@/lib/utils/formato-precio';
import { strategyApplicationsApi } from '../../services/strategy-applications-api';
import type {
  EstrategiaEnBorradorBody,
  ProductoEnBorrador,
  ProductoSimulado,
  ResultadoDelProducto,
} from '../../types/strategy-applications.types';
import { cuerpoDeSimulacion, type BorradorDeEstrategia } from './borrador';
import { BuscadorDeProductos, QUE_FALTA, VerCualesYaTienenOtra } from './ver-cuales';
import { EnlaceAProducto } from '@/components/ui/enlace-a-producto';

/**
 * Paso 4 · Resultados esperados (agregados del 29-sep, spec 232, A4). Producto
 * por producto, lo que haría la estrategia si hoy corriera: es la MISMA
 * simulación que da «si hoy corriera» en los pasos 2 y 3, con el detalle
 * (`conDetalle`), así las cifras de arriba coinciden con las de esos pasos.
 * Tocar una fila la abre a todo el ancho: qué se le aplica (la explicación v2,
 * con la estrategia en borrador), sus reglas y todas las tiendas, que se piden
 * recién ahí (`/simular/producto`). Nada se crea hasta «Crear la estrategia».
 */

const POR_PAGINA = 25;

type Filtro = 'TODOS' | 'BAJAN' | 'SUBEN' | 'REGLA' | 'SIN';
type Orden = 'cambio' | 'baja' | 'sube' | 'nombre';

const sinTildes = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** El cambio en %, contra el precio de hoy. null = no se puede calcular o hoy vale 0. */
export function cambioPct(p: ProductoSimulado): number | null {
  if (p.precioNuevo == null || !(p.precioHoy > 0)) return null;
  return ((p.precioNuevo - p.precioHoy) / p.precioHoy) * 100;
}

const sentido = (p: ProductoSimulado) =>
  p.precioNuevo == null ? null : sentidoDe(p.precioNuevo - p.precioHoy);

export const FILTROS: Array<{
  valor: Filtro;
  texto: string;
  pasa: (p: ProductoSimulado) => boolean;
}> = [
  { valor: 'TODOS', texto: 'Todos', pasa: () => true },
  { valor: 'BAJAN', texto: 'Bajan', pasa: (p) => sentido(p) === 'BAJA' },
  { valor: 'SUBEN', texto: 'Suben', pasa: (p) => sentido(p) === 'SUBE' },
  {
    valor: 'REGLA',
    texto: 'Lo limita una regla',
    pasa: (p) => p.precioNuevo != null && p.limitadoPor.length > 0,
  },
  {
    valor: 'SIN',
    texto: 'No se pueden calcular',
    pasa: (p) => p.resultado === 'NO_SE_PUEDE_CALCULAR',
  },
];

/** El orden de la tabla: los que no se pueden calcular siempre al final. */
export function ordenarResultados(lista: ProductoSimulado[], orden: Orden): ProductoSimulado[] {
  const pct = (p: ProductoSimulado) => cambioPct(p);
  const criterio: Record<Orden, (a: ProductoSimulado, b: ProductoSimulado) => number> = {
    cambio: (a, b) => Math.abs(pct(b) ?? 0) - Math.abs(pct(a) ?? 0),
    baja: (a, b) => (pct(a) ?? 0) - (pct(b) ?? 0),
    sube: (a, b) => (pct(b) ?? 0) - (pct(a) ?? 0),
    nombre: (a, b) => a.name.localeCompare(b.name, 'es'),
  };
  const sin = (p: ProductoSimulado) => (p.resultado === 'NO_SE_PUEDE_CALCULAR' ? 1 : 0);
  return [...lista].sort((a, b) => sin(a) - sin(b) || criterio[orden](a, b));
}

const QUE_PASA: Record<ResultadoDelProducto, { texto: string; tono: TonoDeEtiqueta }> = {
  SE_APLICA_SOLO: { texto: 'Se aplica solo', tono: 'ok' },
  TE_ESPERA: { texto: 'Te espera en Por decidir', tono: 'alerta' },
  SIN_PRECIO_POSIBLE: { texto: 'Conflicto entre reglas', tono: 'bloqueo' },
  YA_ESTA_BIEN: { texto: 'Ya está bien', tono: 'neutro' },
  NO_SE_PUEDE_CALCULAR: { texto: 'No se puede calcular', tono: 'bloqueo' },
};

/** Qué hace el permiso con este cambio, en una línea (debajo de la explicación). */
export function loQueHaceElPermiso(p: ProductoSimulado, body: EstrategiaEnBorradorBody): string {
  const pct = cambioPct(p);
  const cambia = pct == null ? 'El cambio' : `Cambia ${formatoPorcentaje(pct, { signo: true })}`;
  const umbral = body.askAboveChangePct;
  switch (p.resultado) {
    case 'YA_ESTA_BIEN':
      return 'Ya está en ese precio: no cambia nada.';
    case 'SE_APLICA_SOLO':
      return umbral != null
        ? `${cambia}: menos de ${formatoNumero(umbral)} %, se aplica solo.`
        : `${cambia}: se aplica solo.`;
    case 'SIN_PRECIO_POSIBLE':
      return 'Ninguna salida cumple todas tus reglas: te espera en Por decidir para que elijas.';
    case 'NO_SE_PUEDE_CALCULAR':
      return `No se puede calcular: ${QUE_FALTA[p.falta ?? 'OTRO'].texto.toLowerCase()}. Su precio queda en ${formatoPrecio(p.precioHoy)}; la estrategia lo vuelve a intentar en cada corrida.`;
    default:
      if (p.motivoPendiente === 'UMBRAL' && umbral != null)
        return `${cambia}: más de ${formatoNumero(umbral)} %, te espera en Por decidir.`;
      if (p.motivoPendiente === 'LIMITADO')
        return 'Una regla lo deja como está: te espera en Por decidir.';
      // Lote C1 (punto 9): una que aplica sola no aplica debajo del costo ni sin costo.
      if (p.motivoPendiente === 'BAJO_COSTO')
        return `${cambia}: quedaría debajo del costo, no se aplica solo. Te espera en Por decidir.`;
      if (p.motivoPendiente === 'SIN_COSTO')
        return 'No tiene costo: no se aplica solo, te espera en Por decidir.';
      return 'Pediste que te pregunte siempre: te espera en Por decidir.';
  }
}

export function Paso4Resultados({ borrador: b }: { borrador: BorradorDeEstrategia }) {
  const cuerpo = cuerpoDeSimulacion(b);
  // La misma simulación de los pasos 2 y 3, con el detalle.
  const pedido = usePedidoConEspera(
    cuerpo ? JSON.stringify(cuerpo) : null,
    () => strategyApplicationsApi.simular({ ...cuerpo!, conDetalle: true }),
    0
  );
  const [q, setQ] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('TODOS');
  const [orden, setOrden] = useState<Orden>('cambio');
  const [pagina, setPagina] = useState(1);
  const [abierto, setAbierto] = useState<string | null>(null);

  const detalle = useMemo(
    () => (pedido.estado === 'LISTO' ? (pedido.data.detalle ?? []) : []),
    [pedido]
  );
  const cuenta = (f: Filtro) => detalle.filter(FILTROS.find((x) => x.valor === f)!.pasa).length;
  const visibles = useMemo(() => {
    const texto = sinTildes(q.trim());
    const pasa = FILTROS.find((x) => x.valor === filtro)!.pasa;
    return ordenarResultados(
      detalle.filter((p) => pasa(p) && (!texto || sinTildes(`${p.name} ${p.sku}`).includes(texto))),
      orden
    );
  }, [detalle, q, filtro, orden]);
  useEffect(() => setPagina(1), [q, filtro, orden]);

  if (!cuerpo) return null;
  if (pedido.estado === 'ERROR')
    return (
      <EstadoError onReintentar={pedido.reintentar}>
        {mensajeDeError(pedido.error, 'No se pudieron calcular los resultados.')}
      </EstadoError>
    );
  if (pedido.estado !== 'LISTO')
    return (
      <div className="space-y-2" aria-label="Calculando los resultados">
        <Skeleton className="h-16 w-full" />
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );

  const s = pedido.data;
  const h = s.siHoyCorriera;
  // «Te esperan» es la misma cuenta que en el paso 3 (`ReglasQueLaAlcanzan`).
  const esperan = h.superanUmbral + h.tePregunta + h.quedanIgualPorUnaRegla;
  const umbral = cuerpo.askAboveChangePct;
  const quedaronAfuera = cuerpo.onOverlap === 'DEJARLOS' ? s.yaTienenOtra : 0;
  const desde = (pagina - 1) * POR_PAGINA;
  const dePagina = visibles.slice(desde, desde + POR_PAGINA);

  return (
    <div className="space-y-3" data-testid="resultados-esperados">
      {/* El resumen (nombre · método · permiso · horario) va de subtítulo del diálogo
          (maqueta agregados.html#paso4), no en el cuerpo. */}
      <p className="text-sm text-muted-foreground">
        Si hoy corriera, con los precios del mercado y tus reglas de hoy.{' '}
        <strong className="font-semibold text-foreground">Nada cambia hasta que la crees</strong>, y
        después cada precio sigue el permiso que elegiste.
      </p>

      <Cifras role="group" aria-label="Resultados esperados">
        <Cifra
          valor={s.productos}
          rotulo={s.productos === 1 ? 'producto' : 'productos'}
          activa={filtro === 'TODOS'}
          onClick={() => setFiltro('TODOS')}
        />
        <Cifra
          valor={cuenta('BAJAN')}
          rotulo={cuenta('BAJAN') === 1 ? 'baja' : 'bajan'}
          activa={filtro === 'BAJAN'}
          onClick={() => setFiltro('BAJAN')}
        />
        <Cifra
          valor={cuenta('SUBEN')}
          rotulo={cuenta('SUBEN') === 1 ? 'sube' : 'suben'}
          activa={filtro === 'SUBEN'}
          onClick={() => setFiltro('SUBEN')}
        />
        <Cifra
          valor={h.seAplicanSolos}
          rotulo={`${h.seAplicanSolos === 1 ? 'se aplica solo' : 'se aplican solos'}${
            umbral != null && cuerpo.mode === 'AUTO'
              ? `: ${h.seAplicanSolos === 1 ? 'cambia' : 'cambian'} menos de ${formatoNumero(umbral)} %`
              : ''
          }`}
        />
        <Cifra
          valor={esperan}
          tono={esperan > 0 ? 'alerta' : 'neutro'}
          rotulo={esperan === 1 ? 'te espera en Por decidir' : 'te esperan en Por decidir'}
        />
        {h.sinPrecioPosible > 0 && (
          <Cifra valor={h.sinPrecioPosible} tono="bloqueo" rotulo="con un conflicto entre reglas" />
        )}
        <Cifra
          valor={h.yaEstanBien}
          rotulo={h.yaEstanBien === 1 ? 'ya está bien' : 'ya están bien'}
        />
        <Cifra
          valor={s.sinDatos}
          tono={s.sinDatos > 0 ? 'bloqueo' : 'neutro'}
          rotulo={s.sinDatos === 1 ? 'no se puede calcular' : 'no se pueden calcular'}
          activa={filtro === 'SIN'}
          onClick={() => setFiltro('SIN')}
        />
      </Cifras>

      {quedaronAfuera > 0 && (
        <Aviso
          tono="info"
          data-testid="quedaron-afuera"
          titulo={
            quedaronAfuera === 1
              ? '1 no está en esta lista'
              : `${formatoCantidad(quedaronAfuera)} no están en esta lista`
          }
        >
          <span className="flex flex-wrap items-center gap-2">
            {quedaronAfuera === 1
              ? 'Lo dejaste con la estrategia que ya tiene (paso 3).'
              : 'Los dejaste con la estrategia que ya tienen (paso 3).'}
            <VerCualesYaTienenOtra grupo={cuerpo.group} cuantos={quedaronAfuera} enlace />
          </span>
        </Aviso>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <BuscadorDeProductos valor={q} onCambiar={setQ} />
        <GrupoDeOpciones
          aria-label="Qué pasa"
          valor={filtro}
          onCambiar={setFiltro}
          opciones={FILTROS.map((f) => ({
            valor: f.valor,
            texto: f.texto,
            cuenta: cuenta(f.valor),
          }))}
        />
        <SelectNativo
          aria-label="Ordenar"
          className="ml-auto"
          value={orden}
          onChange={(e) => setOrden(e.target.value as Orden)}
        >
          <option value="cambio">El cambio más grande primero</option>
          <option value="baja">Los que más bajan primero</option>
          <option value="sube">Los que más suben primero</option>
          <option value="nombre">Por nombre</option>
        </SelectNativo>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Producto</TableHead>
              <TableHead className="text-right">Hoy (Bs)</TableHead>
              <TableHead className="text-right">Nuevo (Bs)</TableHead>
              <TableHead className="text-right">Cambio</TableHead>
              <TableHead className="text-right">Más barato (Bs)</TableHead>
              <TableHead className="text-right">Tiendas</TableHead>
              <TableHead>Qué pasa</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dePagina.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-muted-foreground">
                  Ningún producto con ese filtro.
                </TableCell>
              </TableRow>
            )}
            {dePagina.map((p) => (
              <Fragment key={p.productId}>
                <FilaDelResultado
                  p={p}
                  abierta={abierto === p.productId}
                  onTocar={() => setAbierto(abierto === p.productId ? null : p.productId)}
                />
                {abierto === p.productId && (
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableCell colSpan={7} className="px-3 pb-4 pt-2">
                      <FilaAbierta p={p} body={cuerpo} nombre={b.nombre} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      <Pagination
        page={pagina}
        totalPages={Math.max(1, Math.ceil(visibles.length / POR_PAGINA))}
        totalItems={visibles.length}
        pageSize={POR_PAGINA}
        onPageChange={setPagina}
        itemName={visibles.length === 1 ? 'producto' : 'productos'}
      />
    </div>
  );
}

function FilaDelResultado({
  p,
  abierta,
  onTocar,
}: {
  p: ProductoSimulado;
  abierta: boolean;
  onTocar: () => void;
}) {
  const que = QUE_PASA[p.resultado];
  const pct = cambioPct(p);
  return (
    <TableRow data-testid="fila-resultado" className="cursor-pointer" onClick={onTocar}>
      <TableCell>
        <button
          type="button"
          aria-expanded={abierta}
          className="flex items-start gap-1.5 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(e) => {
            e.stopPropagation();
            onTocar();
          }}
        >
          {/* La flecha que dice que la fila se abre (1oct-B9); gira al abrirla. */}
          <ChevronRight
            aria-hidden
            className={cn(
              'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              abierta && 'rotate-90'
            )}
          />
          <CeldaProducto
            nombre={p.name}
            detalle={p.category ? `${p.sku} · ${p.category}` : p.sku}
          />
        </button>
      </TableCell>
      <TableCell className="text-right">
        <Precio valor={p.precioHoy} />
      </TableCell>
      <TableCell className="text-right font-semibold">
        <Precio valor={p.precioNuevo} />
      </TableCell>
      <TableCell className="text-right">
        {p.precioNuevo == null ? (
          <span className="text-muted-foreground">—</span>
        ) : sentidoDe(p.precioNuevo - p.precioHoy) === 'IGUAL' ? (
          <span className="text-muted-foreground">igual</span>
        ) : (
          <CambioDePrecio diferencia={p.precioNuevo - p.precioHoy} porcentaje={pct} />
        )}
      </TableCell>
      <TableCell className="text-right">
        <Precio valor={p.masBarato} />
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {p.tiendas > 0 ? (
          formatoCantidad(p.tiendas)
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <Etiqueta tono={que.tono}>{que.texto}</Etiqueta>
        {p.precioNuevo != null && p.limitadoPor.length > 0 && (
          <div className="mt-0.5 text-xs text-muted-foreground">
            lo limita {p.limitadoPor.map((n) => `«${n}»`).join(' y ')}
          </div>
        )}
        {p.resultado === 'NO_SE_PUEDE_CALCULAR' && (
          <div className="mt-0.5 text-xs text-muted-foreground">
            {QUE_FALTA[p.falta ?? 'OTRO'].texto}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * La fila abierta: qué se le aplica (la explicación v2 sobre la fila que da
 * `/simular/producto`, la misma cuenta), qué hace el permiso, sus reglas y
 * todas las tiendas contra el precio nuevo.
 */
function FilaAbierta({
  p,
  body,
  nombre,
}: {
  p: ProductoSimulado;
  body: EstrategiaEnBorradorBody;
  nombre: string;
}) {
  const [estado, setEstado] = useState<
    | { tipo: 'CARGANDO' }
    | { tipo: 'LISTO'; data: ProductoEnBorrador }
    | { tipo: 'ERROR'; error: unknown }
  >({ tipo: 'CARGANDO' });
  const [vuelta, setVuelta] = useState(0);
  // Un texto, no el objeto: el cuerpo se arma de nuevo en cada render del paso.
  const pedido = JSON.stringify({
    strategy: body.strategy,
    strategyConfig: body.strategyConfig,
    mode: body.mode,
    askAboveChangePct: body.askAboveChangePct,
    productId: p.productId,
    ...(nombre.trim() && { name: nombre.trim() }),
  });
  useEffect(() => {
    let vivo = true;
    setEstado({ tipo: 'CARGANDO' });
    strategyApplicationsApi
      .simularProducto(JSON.parse(pedido))
      .then((data) => vivo && setEstado({ tipo: 'LISTO', data }))
      .catch((error: unknown) => vivo && setEstado({ tipo: 'ERROR', error }));
    return () => {
      vivo = false;
    };
  }, [pedido, vuelta]);

  if (estado.tipo === 'ERROR')
    return (
      <EstadoError onReintentar={() => setVuelta((v) => v + 1)}>
        {mensajeDeError(estado.error, 'No se pudo traer el detalle de este producto.')}
      </EstadoError>
    );
  if (estado.tipo === 'CARGANDO')
    return (
      <div className="grid grid-cols-2 gap-4" aria-label="Cargando el detalle">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );

  const { fila, tiendas } = estado.data;
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="fila-abierta">
      <div className="space-y-3">
        <section aria-label="Qué se le aplica" className="space-y-2">
          <h4 className="text-sm font-semibold">Qué se le aplica</h4>
          <PorQueEstePrecio recorrido={desdeFilaSimulada(fila)} tiempo="SI_CORRIERA" />
          <p className="text-sm text-muted-foreground" data-testid="lo-que-hace-el-permiso">
            {loQueHaceElPermiso(p, body)}
          </p>
        </section>
        <SusReglas fila={estado.data.fila} sinCalcular={p.resultado === 'NO_SE_PUEDE_CALCULAR'} />
      </div>
      <TodasLasTiendas tiendas={tiendas} precioNuevo={p.precioNuevo} precioHoy={p.precioHoy} />
    </div>
  );
}

/**
 * Todas las reglas que lo alcanzan: si se cumple, si limita el precio, o si no se puede revisar.
 * Un producto que no se puede calcular trae `rangos: []`: decir «ninguna regla lo alcanza»
 * sería falso, así que se dice que no hay precio que revisar (1oct-B3).
 */
function SusReglas({
  fila,
  sinCalcular = false,
}: {
  fila: ProductoEnBorrador['fila'];
  sinCalcular?: boolean;
}) {
  if (sinCalcular)
    return (
      <section aria-label="Sus reglas" className="space-y-2">
        <h4 className="text-sm font-semibold">Sus reglas</h4>
        <p className="text-sm text-muted-foreground">
          Sin precio calculado no hay reglas que revisar: se revisan cuando la estrategia pueda
          calcularlo.
        </p>
      </section>
    );
  const porRegla = new Map<string, typeof fila.rangos>();
  for (const r of fila.rangos) porRegla.set(r.ruleId, [...(porRegla.get(r.ruleId) ?? []), r]);
  const reglas = [...porRegla];
  return (
    <section aria-label="Sus reglas" className="space-y-2">
      <h4 className="text-sm font-semibold">
        Sus reglas{' '}
        <span className="font-normal text-muted-foreground">
          · {cantidadDe(reglas.length, 'regla lo alcanza', 'reglas lo alcanzan')}
        </span>
      </h4>
      {reglas.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ninguna regla lo alcanza.</p>
      ) : (
        <ul className="divide-y rounded-md border bg-card text-sm">
          {reglas.map(([id, rangos]) => {
            const limita = fila.limitantes.includes(id);
            const sinDato = rangos.every((r) => r.aviso != null);
            const detalle = sinDato
              ? rangos[0].aviso
              : rangos
                  .flatMap((r) => [
                    r.min != null ? `no baja de ${formatoPrecio(r.min)}` : null,
                    r.max != null ? `no sube de ${formatoPrecio(r.max)}` : null,
                  ])
                  .filter(Boolean)
                  .join(' · ');
            return (
              <li key={id} className="flex items-start justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <div className="font-medium">{rangos[0].ruleName}</div>
                  {detalle && <div className="text-xs text-muted-foreground">{detalle}</div>}
                </div>
                <Etiqueta tono={limita ? 'alerta' : sinDato ? 'neutro' : 'ok'}>
                  {limita ? 'Limita el precio' : sinDato ? 'No se puede revisar' : 'Se cumple'}
                </Etiqueta>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * Todas las tiendas, del más barato al más caro, con tu precio nuevo y el de hoy
 * en su lugar: «quedás 3.º de 6» y el promedio. Sin lecturas, lo dice.
 */
function TodasLasTiendas({
  tiendas,
  precioNuevo,
  precioHoy,
}: {
  tiendas: ProductoEnBorrador['tiendas'];
  precioNuevo: number | null;
  precioHoy: number;
}) {
  if (tiendas.length === 0)
    return (
      <section aria-label="Todas las tiendas" className="space-y-2">
        <h4 className="text-sm font-semibold">Todas las tiendas</h4>
        <EstadoVacio titulo="Ninguna lectura de la competencia">
          Ninguna tienda seguida tiene un precio leído de este producto.
        </EstadoVacio>
      </section>
    );
  type Fila = {
    clave: string;
    nombre: string;
    precio: number;
    leidoEn?: string | null;
    url?: string | null;
    mio?: 'NUEVO' | 'HOY';
  };
  const filas: Fila[] = tiendas.map((t, i) => ({
    clave: `t${i}`,
    nombre: t.nombre,
    precio: t.precio,
    leidoEn: t.leidoEn,
    url: t.url,
  }));
  if (precioNuevo != null)
    filas.push({ clave: 'nuevo', nombre: 'Tu precio nuevo', precio: precioNuevo, mio: 'NUEVO' });
  filas.push({ clave: 'hoy', nombre: 'Tu precio hoy', precio: precioHoy, mio: 'HOY' });
  // Del más barato al más caro; en un empate, tu precio nuevo primero.
  filas.sort((a, b) => a.precio - b.precio || (a.mio === 'NUEVO' ? -1 : b.mio === 'NUEVO' ? 1 : 0));
  const promedio = tiendas.reduce((acc, t) => acc + t.precio, 0) / tiendas.length;
  const puesto =
    precioNuevo == null ? null : tiendas.filter((t) => t.precio < precioNuevo - 0.004).length + 1;
  const contra = (precio: number) =>
    precioNuevo == null || !(precioNuevo > 0)
      ? '—'
      : formatoPorcentaje(((precio - precioNuevo) / precioNuevo) * 100, { signo: true });
  return (
    <section aria-label="Todas las tiendas" className="space-y-2">
      <h4 className="text-sm font-semibold">
        Todas las tiendas{' '}
        <span className="font-normal text-muted-foreground">
          · {cantidadDe(tiendas.length, 'tienda', 'tiendas')}, del más barato al más caro
        </span>
      </h4>
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tienda</TableHead>
              <TableHead className="text-right">Precio (Bs)</TableHead>
              <TableHead className="text-right">Contra tu precio nuevo</TableHead>
              <TableHead>Leído</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.map((f) => (
              <TableRow
                key={f.clave}
                data-mio={f.mio}
                className={cn(
                  f.mio === 'NUEVO' && 'bg-primary/10 font-semibold hover:bg-primary/10',
                  f.mio === 'HOY' && 'text-muted-foreground'
                )}
              >
                <TableCell>{f.nombre}</TableCell>
                <TableCell className="text-right">
                  <EnlaceAProducto href={f.url} tienda={f.nombre}>
                    <Precio valor={f.precio} />
                  </EnlaceAProducto>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {f.mio === 'NUEVO' ? '—' : contra(f.precio)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {f.mio ? '' : f.leidoEn ? haceCuanto(f.leidoEn) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="puesto">
        {puesto == null
          ? 'Sin precio nuevo no hay puesto.'
          : puesto === 1
            ? 'Con el precio nuevo quedás el más barato.'
            : `Con el precio nuevo quedás ${formatoCantidad(puesto)}.º de ${formatoCantidad(tiendas.length + 1)}.`}{' '}
        Promedio del mercado {formatoPrecio(promedio)}.
      </p>
    </section>
  );
}
